import { CommonModule } from '@angular/common';
import { Injector, NgModule } from '@angular/core';
import { createCustomElement } from '@angular/elements';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DcAlertModule } from '@datachain/ui-sdk/components/alert';
import { DcBadgeModule } from '@datachain/ui-sdk/components/app-badge';
import { DcButtonModule } from '@datachain/ui-sdk/components/app-button';
import { DcCardOutlineModule } from '@datachain/ui-sdk/components/app-card-outline';
import { DcChipModule } from '@datachain/ui-sdk/components/app-chip';
import { DcColumnTypeModule } from '@datachain/ui-sdk/components/app-column-type';
import { DcColumnsConfiguratorModule } from '@datachain/ui-sdk/components/app-columns-configurator';
import { DcMultiSelectMenuModule } from '@datachain/ui-sdk/components/app-multi-select-menu';
import { DcOverlayBadgeModule } from '@datachain/ui-sdk/components/app-overlay-badge';
import { DcPageModule } from '@datachain/ui-sdk/components/app-page';
import { DcPanelModule } from '@datachain/ui-sdk/components/app-panel';
import { DcPopoverModule } from '@datachain/ui-sdk/components/app-popover';
import { DcToggleButtonsModule } from '@datachain/ui-sdk/components/app-toggle-buttons';
import { DcTooltipModule } from '@datachain/ui-sdk/components/app-tooltip';
import { DcAvatarModule } from '@datachain/ui-sdk/components/avatar';
import { DcBreadcrumbModule } from '@datachain/ui-sdk/components/breadcrumb';
import { DcViewerComponentModule } from '@datachain/ui-sdk/components/viewer';
import {
	DcMaxLinesToggleModule,
	DcNavigateDirectiveModule,
} from '@datachain/ui-sdk/directives';
import {
	DcArrayPipeModule,
	DcDayjsDatePipeModule,
	DcTruncatePipeModule,
} from '@datachain/ui-sdk/pipes';
import { EffectsModule } from '@ngrx/effects';
import { StoreModule } from '@ngrx/store';
import {
	DxButtonModule,
	DxDataGridModule,
	DxSelectBoxModule,
	DxTextBoxModule,
	DxValidatorModule,
} from 'devextreme-angular';

import { MaterialModule } from '../ui/material.module';
import { UiModule } from '../ui/ui.module';
import { AccessConsumerMappingPopupComponent } from './components/exposition-access-config/access-consumer-mapping-popup/access-consumer-mapping-popup.component';
import { ExpositionAccessConfigComponent } from './components/exposition-access-config/exposition-access-config.component';
import { ExpositionConsumerListComponent } from './components/exposition-access-config/exposition-consumers-list/exposition-consumer-list.component';
import { ExpositionActionConfirmationPopupComponent } from './components/exposition-action-confirmation-popup/exposition-action-confirmation-popup.component';
import { ExpositionColumnsConfigExtendedComponent } from './components/exposition-columns-config-extended/exposition-columns-config-extended.component';
import { ExpositionColumnsConfigComponent } from './components/exposition-columns-config/exposition-columns-config.component';
import { ConsumerMappingPopupComponent } from './components/exposition-consumers-mapping/consumer-mapping-popup/consumer-mapping-popup.component';
import { ExpositionConsumersMappingComponent } from './components/exposition-consumers-mapping/exposition-consumers-mapping.component';
import { ExpositionProjectImportConsumersMappingComponent } from './components/exposition-consumers-mapping/exposition-project-import-consumers-mapping/exposition-project-import-consumers-mapping.component';
// eslint-disable-next-line max-len
import { ExpositionEndpointMetadataConfigComponent } from './components/exposition-endpoint-metadata-config/exposition-endpoint-metadata-config.component';
// eslint-disable-next-line max-len
import { ExpositionHistoryListComponent } from './components/exposition-history-list/exposition-history-list.component';
import { ExpositionInternalMetadataConfigComponent } from './components/exposition-internal-metadata-config/exposition-internal-metadata.config.component';
import { ExpositionListComponent } from './components/exposition-list/exposition-list.component';
import { ExpositionPublicationStatusComponent } from './components/exposition-publication-status/exposition-publication-status.component';
import { ExpositionSimulatorComponent } from './components/exposition-simulator/exposition-simulator.component';
import { ExpositionDatablockListComponent } from './components/exposition-stepper/exposition-datablock-list/exposition-datablock-list.component';
import { ExpositionStepperComponent } from './components/exposition-stepper/exposition-stepper.component';
import { ExpositionAccessListComponent } from './components/exposition-view/exposition-access-list/exposition-access-list.component';
import { ExpositionAccessModalComponent } from './components/exposition-view/exposition-access-modal/exposition-access-modal.component';
import { ExpositionColumnsModalComponent } from './components/exposition-view/exposition-columns-modal/exposition-columns-modal.component';
import { ExpositionEndpointMetadataPopupComponent } from './components/exposition-view/exposition-endpoint-metadata-popup/exposition-endpoint-metadata-popup.component';
import { ExpositionMetadataPopupComponent } from './components/exposition-view/exposition-metadata-popup/exposition-metadata-popup.component';
import { ExpositionViewComponent } from './components/exposition-view/exposition-view.component';
import { PublishedExpositionComponent } from './components/exposition-view/published-exposition/published-exposition.component';
import { ExpositionsAdapter } from './expositions.adapter';
import { NgExpositions } from './ng-expositions';
import {
	ExpositionFeatureKey,
	expositionReducers,
	ExpositionsEffects,
	ExpositionsSelector,
	SimulatorEffects,
	SimulatorSelector,
} from './store';

@NgModule({
	declarations: [
		ExpositionListComponent,
		ExpositionStepperComponent,
		ExpositionDatablockListComponent,
		ExpositionInternalMetadataConfigComponent,
		ExpositionEndpointMetadataConfigComponent,
		ExpositionColumnsConfigExtendedComponent,
		ExpositionColumnsConfigComponent,
		ExpositionAccessListComponent,
		ExpositionAccessConfigComponent,
		ExpositionConsumerListComponent,
		ExpositionViewComponent,
		ExpositionColumnsModalComponent,
		ExpositionAccessModalComponent,
		PublishedExpositionComponent,
		ExpositionMetadataPopupComponent,
		ExpositionEndpointMetadataPopupComponent,
		ExpositionPublicationStatusComponent,
		ExpositionActionConfirmationPopupComponent,
		ExpositionHistoryListComponent,
		ExpositionConsumersMappingComponent,
		ConsumerMappingPopupComponent,
		ExpositionProjectImportConsumersMappingComponent,
		ExpositionSimulatorComponent,
		AccessConsumerMappingPopupComponent,
	],
	imports: [
		CommonModule,
		ReactiveFormsModule,
		StoreModule.forFeature(ExpositionFeatureKey, expositionReducers),
		EffectsModule.forFeature([ExpositionsEffects, SimulatorEffects]),
		MaterialModule,
		FormsModule,
		DxSelectBoxModule,
		DxDataGridModule,
		DxButtonModule,
		DxTextBoxModule,
		DxValidatorModule,
		UiModule,
		DcCardOutlineModule,
		DcPanelModule,
		DcToggleButtonsModule,
		DcMultiSelectMenuModule,
		DcColumnsConfiguratorModule,
		DcArrayPipeModule,
		DcPageModule,
		DcTruncatePipeModule,
		DcAvatarModule,
		DcBadgeModule,
		DcOverlayBadgeModule,
		DcPopoverModule,
		DcColumnTypeModule,
		DcBreadcrumbModule,
		DcTooltipModule,
		DcNavigateDirectiveModule,
		DcChipModule,
		DcButtonModule,
		DcViewerComponentModule,
		DcMaxLinesToggleModule,
		DcDayjsDatePipeModule,
		DcAlertModule,
	],
	providers: [
		NgExpositions,
		ExpositionsSelector,
		SimulatorSelector,
		ExpositionsAdapter,
	],
})
export class ExpositionsModule {
	public constructor(private readonly injector: Injector) {
		const expositionsListCmpElement = createCustomElement(
			ExpositionListComponent,
			{
				injector,
			}
		);
		customElements.define('app-expositions-list', expositionsListCmpElement);

		const expositionViewCmpElement = createCustomElement(
			ExpositionViewComponent,
			{
				injector,
			}
		);
		customElements.define('app-exposition-view', expositionViewCmpElement);

		const expositionHistoryCmpElement = createCustomElement(
			ExpositionHistoryListComponent,
			{
				injector,
			}
		);
		customElements.define(
			'app-exposition-history-list',
			expositionHistoryCmpElement
		);

		const expositionConsumersMappingCmpElement = createCustomElement(
			ExpositionProjectImportConsumersMappingComponent,
			{
				injector,
			}
		);
		customElements.define(
			'app-project-consumers-mapping',
			expositionConsumersMappingCmpElement
		);

		const expositionDataSimulatorCmpElement = createCustomElement(
			ExpositionSimulatorComponent,
			{
				injector,
			}
		);
		customElements.define(
			'app-exposition-simulator',
			expositionDataSimulatorCmpElement
		);
	}
}
